/**
 * 샘플 점검 요청의 실패 → 사용자 문구.
 *
 * 코드 → 문구 매핑을 이 파일 **한 곳**이 소유한다(규칙 §11). 서버 원문을 그대로 쓰지 않는다(규칙 §5).
 *
 * Owns:
 * - inspect · media 오류 코드와 통신 실패의 문구, 다시 받아야 하는 실패의 판정.
 * - 저장된 매핑을 읽지 · 저장하지 · 지우지 · 검증하지 못했을 때, POC 를 접수하거나 따라가지 못했을 때의 문구.
 *
 * Does not own:
 * - Dataset · Probe 오류 — `datasets` 모듈. 만료 판정 — `domain/episodeInspect.ts`.
 */
import { isHttpClientError } from "@/shared/http";

const MESSAGE_BY_CODE: Record<string, string> = {
  "probe.not_found": "요청한 Probe 를 찾을 수 없습니다.",
  "probe.sample_not_ready":
    "이 Probe 는 아직 샘플을 확보하지 않았습니다. 샘플이 확보되면 점검할 수 있습니다.",
  "episode.not_found": "이 Probe 의 샘플에 없는 episode 입니다. 다른 episode 를 골라 주세요.",
  "inspect.not_found":
    "이 샘플에는 점검 데이터가 없습니다. 점검 데이터가 생기기 전에 확보한 샘플이라 새 Probe 를 요청해 주세요.",
  "inspect.unsupported_schema":
    "지금 화면이 읽는 형식보다 이전에 확보한 샘플입니다. 새 Probe 를 요청하면 점검할 수 있습니다.",
  "storage.account_mismatch":
    "샘플 저장소 설정이 맞지 않아 읽을 수 없습니다. 운영자에게 알려 주세요.",
  "media.grant_invalid": "영상 주소가 만료되었습니다. 점검 데이터를 다시 받아 주세요.",
  "media.not_found": "영상 파일을 찾을 수 없습니다. 새 Probe 를 요청해 주세요.",
  "auth.jwks.unavailable":
    "인증 서버에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요. 로그인은 유지됩니다.",
};

export function resolveInspectErrorMessage(error: unknown): string {
  if (!isHttpClientError(error)) {
    return "점검 데이터를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.";
  }
  const byCode = error.code === null ? undefined : MESSAGE_BY_CODE[error.code];
  if (byCode !== undefined) {
    return byCode;
  }
  switch (error.kind) {
    case "network":
      return "서버에 연결하지 못했습니다. 네트워크 상태를 확인해 주세요.";
    case "timeout":
      return "요청 시간이 초과되었습니다. 잠시 후 다시 시도해 주세요.";
    case "server":
      return "서버에서 요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.";
    default:
      return "점검 데이터를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.";
  }
}

/** 다시 받으면 풀릴 수 있는 실패인지 — 재시도 버튼을 낼지 정한다. 계약 위반 · 없는 리소스는 아니다. */
export function isInspectRetryable(error: unknown): boolean {
  if (!isHttpClientError(error)) {
    return false;
  }
  if (error.code === "auth.jwks.unavailable") {
    return true;
  }
  return error.kind === "network" || error.kind === "timeout" || error.kind === "server";
}

const MAPPING_LOAD_MESSAGE_BY_CODE: Record<string, string> = {
  "revision.not_found":
    "이 Probe 의 Revision 을 찾을 수 없어 저장된 매핑을 읽지 못했습니다. 새 Probe 를 요청해 주세요.",
  "mapping.invalid":
    "저장된 매핑을 지금의 매핑 형식으로 읽지 못했습니다. 저장을 삭제한 뒤 빈 매핑에서 새로 작성해 주세요.",
};

/** 저장된 매핑을 읽지 못한 이유. 점검 데이터는 이 실패와 관계없이 보인다. */
export function resolveMappingLoadMessage(error: unknown): string {
  const byCode =
    isHttpClientError(error) && error.code !== null
      ? MAPPING_LOAD_MESSAGE_BY_CODE[error.code]
      : undefined;
  return byCode ?? "저장된 매핑을 읽지 못했습니다. 잠시 후 점검 화면을 다시 열어 주세요.";
}

/** 통신 실패의 공통 문구. 매핑 명령은 코드가 없으면 이 문구로 떨어진다. */
function transportMessage(error: unknown, fallback: string): string {
  if (!isHttpClientError(error)) {
    return fallback;
  }
  switch (error.kind) {
    case "network":
      return "서버에 연결하지 못했습니다. 네트워크 상태를 확인한 뒤 다시 시도해 주세요.";
    case "timeout":
      return "요청 시간이 초과되었습니다. 잠시 후 다시 시도해 주세요.";
    case "server":
      return "서버에서 요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.";
    default:
      return fallback;
  }
}

const MAPPING_SAVE_MESSAGE_BY_CODE: Record<string, string> = {
  "revision.not_found":
    "이 Probe 의 Revision 을 찾을 수 없어 매핑을 저장하지 못했습니다. 새 Probe 를 요청해 주세요.",
  "request.validation_failed":
    "서버가 매핑의 모양을 받지 않았습니다. 값을 확인한 뒤 다시 저장해 주세요. 편집한 값은 그대로 있습니다.",
  "mapping.invalid":
    "빈 필수 칸이 있어 저장하지 못했습니다. 아래 항목을 채운 뒤 다시 저장해 주세요. 편집한 값은 그대로 있습니다.",
};

/** 매핑을 저장하지 못한 이유. 편집본은 화면에 남는다. */
export function resolveMappingSaveMessage(error: unknown): string {
  const byCode =
    isHttpClientError(error) && error.code !== null
      ? MAPPING_SAVE_MESSAGE_BY_CODE[error.code]
      : undefined;
  return (
    byCode ??
    transportMessage(
      error,
      "매핑을 저장하지 못했습니다. 편집한 값은 그대로 있습니다. 다시 저장해 주세요.",
    )
  );
}

/** 저장된 매핑을 지우지 못한 이유. */
export function resolveMappingDeleteMessage(error: unknown): string {
  if (isHttpClientError(error) && error.code === "revision.not_found") {
    return "이 Probe 의 Revision 을 찾을 수 없어 저장된 매핑을 지우지 못했습니다.";
  }
  return transportMessage(error, "저장된 매핑을 지우지 못했습니다. 잠시 후 다시 시도해 주세요.");
}

const MAPPING_VALIDATE_MESSAGE_BY_CODE: Record<string, string> = {
  "mapping.invalid":
    "검사할 episode 가 이 Probe 의 샘플에 없습니다. 점검 화면을 다시 연 뒤 검증해 주세요.",
  "revision.not_found":
    "Revision 이나 Probe 샘플을 찾을 수 없어 검증하지 못했습니다. Probe 가 샘플을 확보했는지 확인해 주세요.",
  "request.validation_failed":
    "서버가 매핑의 모양을 받지 않았습니다. 값을 확인한 뒤 다시 검증해 주세요.",
  "storage.account_mismatch":
    "샘플 저장소 설정이 맞지 않아 검증하지 못했습니다. 운영자에게 알려 주세요.",
};

/** 검증 요청이 실패한 이유. 매핑 문제는 실패가 아니라 결과의 issue 로 온다. */
export function resolveMappingValidateMessage(error: unknown): string {
  const byCode =
    isHttpClientError(error) && error.code !== null
      ? MAPPING_VALIDATE_MESSAGE_BY_CODE[error.code]
      : undefined;
  return (
    byCode ?? transportMessage(error, "매핑을 검증하지 못했습니다. 잠시 후 다시 시도해 주세요.")
  );
}

const POC_REQUEST_MESSAGE_BY_CODE: Record<string, string> = {
  "mapping.invalid":
    "매핑이 완성되지 않았거나 샘플에 episode 가 없어 POC 를 접수하지 못했습니다. 먼저 검증으로 빈 칸을 확인해 주세요.",
  "http.not_found": "Revision 이나 Probe 를 찾을 수 없어 POC 를 접수하지 못했습니다.",
  "request.validation_failed":
    "서버가 매핑의 모양을 받지 않았습니다. 값을 확인한 뒤 다시 요청해 주세요.",
  "storage.account_mismatch":
    "샘플 저장소 설정이 맞지 않아 POC 를 접수하지 못했습니다. 운영자에게 알려 주세요.",
};

/** POC 를 접수하지 못한 이유. */
export function resolvePocRequestMessage(error: unknown): string {
  const byCode =
    isHttpClientError(error) && error.code !== null
      ? POC_REQUEST_MESSAGE_BY_CODE[error.code]
      : undefined;
  return (
    byCode ?? transportMessage(error, "POC 를 접수하지 못했습니다. 잠시 후 다시 시도해 주세요.")
  );
}

/** POC 상태를 받지 못한 이유. 폴링은 다음 주기에 다시 묻는다. */
export function resolvePocPollMessage(error: unknown): string {
  if (isHttpClientError(error) && error.code === "http.not_found") {
    return "POC 작업을 찾을 수 없습니다. 다시 요청해 주세요.";
  }
  return "POC 상태를 받지 못했습니다. 다음 주기에 다시 묻습니다.";
}
